import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { createWatchdogTheme } from '../theme';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import SettingsIcon from '@mui/icons-material/Settings';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, Cell, ReferenceLine } from 'recharts';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../assets/watchdog_logo_yellow.png';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Link from '@mui/material/Link';
import WatchdogNoData from './WatchdogNoData';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import InfoIcon from '@mui/icons-material/Info';
import TableContainer from '@mui/material/TableContainer';
import { Tooltip as MUITooltip } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import Popover from '@mui/material/Popover';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';

// Move all the dummy data here
// const shareOfVoiceData = [
//   { name: 'Prensa', value: 33.11, color: '#FFB347' },
//   { name: 'Internet', value: 17.1, color: '#87CEEB' },
//   { name: 'Radio', value: 23.6, color: '#F0E68C' },
//   { name: 'Television', value: 26.2, color: '#98FB98' }
// ];

const sentimentTrendData = [
  { date: 'Nov 3', Prensa: 0.1, Radio: 0.05, Television: -0.05, Internet: 0.15 },
  { date: 'Nov 10', Prensa: 0.15, Radio: 0.1, Television: 0.1, Internet: 0.3 },
  { date: 'Nov 17', Prensa: -0.1, Radio: -0.15, Television: -0.2, Internet: -0.1 },
  { date: 'Nov 24', Prensa: 0.2, Radio: 0.15, Television: 0.25, Internet: 0.05 },
  { date: 'Dec 1', Prensa: 0.05, Radio: 0, Television: 0.1, Internet: -0.05 }
];

const sentimentDistributionData = [
  { sentiment: 'Positive', value: 35 },
  { sentiment: 'Neutral', value: 45 },
  { sentiment: 'Negative', value: 20 }
];

const topChannelsData = [
  { channel: 'WKAQ', sentiment: 0.8 },
  { channel: 'WAPA', sentiment: 0.75 },
  { channel: 'Telemundo', sentiment: 0.7 },
  { channel: 'NotiCel', sentiment: 0.65 },
  { channel: 'El Nuevo Día', sentiment: 0.6 },
  { channel: 'Primera Hora', sentiment: 0.55 },
  { channel: 'Metro', sentiment: 0.5 },
  { channel: 'El Vocero', sentiment: 0.45 },
  { channel: 'Radio Isla', sentiment: 0.4 },
  { channel: 'WIPR', sentiment: 0.35 }
];

const bottomChannelsData = [
  { channel: 'La Perla del Sur', sentiment: -0.35 },
  { channel: 'Caribbean Business', sentiment: -0.3 },
  { channel: 'Sin Comillas', sentiment: -0.25 },
  { channel: 'News Is My Business', sentiment: -0.2 },
  { channel: 'El Post Antillano', sentiment: -0.15 },
  { channel: 'La Esquina Digital', sentiment: -0.1 },
  { channel: 'Periodismo Investigativo', sentiment: -0.08 },
  { channel: 'Claridad', sentiment: -0.05 },
  { channel: 'Diálogo Digital', sentiment: -0.03 },
  { channel: 'Noticias Online', sentiment: -0.01 }
];

const programShareOfVoiceData = [
  { program: 'El Circo', positive: 40, neutral: 35, negative: 25 },
  { program: 'El Despelote', positive: 35, neutral: 40, negative: 25 },
  { program: 'La Tendencia', positive: 30, neutral: 45, negative: 25 },
  { program: 'Sunset Drive', positive: 25, neutral: 50, negative: 25 },
  { program: 'El Refugio', positive: 20, neutral: 55, negative: 25 }
];

const programSentimentRankingData = [
  { program: 'El Circo', sentiment: 0.8 },
  { program: 'El Despelote', sentiment: 0.75 },
  { program: 'La Tendencia', sentiment: 0.7 },
  { program: 'Sunset Drive', sentiment: 0.65 },
  { program: 'El Refugio', sentiment: 0.5 },
  { program: 'La Comay', sentiment: 0.45 },
  { program: 'Las Noticias Ahora', sentiment: 0.4 },
  { program: 'Si no lo digo reviento', sentiment: 0.35 },
  { program: 'Temprano en la mañana', sentiment: 0.3 },
  { program: 'Las Noticias al Medio Día', sentiment: 0.2 }
];

interface FilterParams {
  userId?: string;
  mediaType?: string;
  channel?: string;
  program?: string;
  topic?: string;
  startDate?: string;
  endDate?: string;
  dateRange?: string;
}

// Add interface for chart data
interface ChartData {
  share_of_voice: {
    chart_type: string;
    title: string;
    data: { [key: string]: number };
  };
  sentiment_distribution: {
    chart_type: string;
    title: string;
    data: Array<{ name: string; value: number }>;
  };
  sentiment_trend: {
    chart_type: string;
    title: string;
    data: {
      day: SentimentTrendData[];
      week: SentimentTrendData[];
      month: SentimentTrendData[];
    };
    config: {
      colors: { [key: string]: string };
    };
  };
  channel_comparison: {
    total_channels: number;
    top_channels: Array<{ channel: string; sentiment: number }>;
    bottom_channels: Array<{ channel: string; sentiment: number }>;
  };
  program_breakdown: {
    share_of_voice: Array<{
      program: string;
      positive: number;
      neutral: number;
      negative: number;
    }>;
    sentiment_ranking: Array<{
      program: string;
      sentiment: number;
    }>;
  };
  recent_alerts: Array<{
    date: string;
    display_date: string;
    media_type: string;
    channel: string;
    program: string;
    sentiment: number;
    title: string;
    topics: string[];
    alert_id: number;
  }>;
}

// Add new interface for sentiment distribution data
interface SentimentDistributionData {
  name: string;
  value: number;
}

// Update interface definitions
interface SentimentTrendData {
  date: string;
  [key: string]: number | string | null;  // For dynamic media type columns
}

interface SentimentTrendState {
  day: SentimentTrendData[];
  week: SentimentTrendData[];
  month: SentimentTrendData[];
}

// Add new type for metric
type MetricType = 'sentiment' | 'alert_count';

// Add interface for API response
interface ApiResponse {
  no_data?: boolean;
  message?: string;
  chart_data?: ChartData;
}

function WatchdogDashboard() {
  const [searchParams] = useSearchParams();
  const [darkMode, setDarkMode] = useState(false);
  const [filterParams, setFilterParams] = useState<FilterParams>({});
  const [isValidUser, setIsValidUser] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [sentimentDistribution, setSentimentDistribution] = useState<SentimentDistributionData[]>([]);
  const [sentimentTrend, setSentimentTrend] = useState<SentimentTrendState>({
    day: [],
    week: [],
    month: []
  });
  const [timeGrouping, setTimeGrouping] = useState<'day' | 'week' | 'month'>('day');
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('sentiment');
  const [noDataMessage, setNoDataMessage] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'connection' | 'missing_user' | 'invalid_user' | null>(null);
  const [chartWidth, setChartWidth] = useState(window.innerWidth * 0.85);
  // Table sorting variables
  const [orderBy, setOrderBy] = useState<'date' | 'media_type' | 'channel' | 'program' | 'sentiment' | 'title' | 'topics'>('date');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Topic filter variables
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [showTopicFilter, setShowTopicFilter] = useState(false);
  const topicFilterRef = useRef<HTMLDivElement>(null);
  const topicCellRef = useRef<HTMLTableCellElement>(null);

  // Add resize listener
  useEffect(() => {
    const handleResize = () => {
      setChartWidth(window.innerWidth * 0.85);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = useCallback(async (params: FilterParams) => {
    try {
      const userId = searchParams.get('userId');
      if (!userId) {
        setErrorType('missing_user');
        setIsValidUser(false);
        setIsValidating(false);
        return;
      }
      const response = await fetch(`${process.env.REACT_APP_API_SERVER_URL}/api/watchdog/filter?userId=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ ...params, grouping: timeGrouping })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        if (response.status === 401) {
          setErrorType('invalid_user');
        } else {
          setErrorType('connection');
        }
        setIsValidUser(false);
        setIsValidating(false);
        throw new Error(`Network response was not ok: ${response.status}`);
      }

      const data: ApiResponse = await response.json();
      console.log('Response from backend:', data);
      
      if (data.no_data) {
        setNoDataMessage(data.message || 'No data available for the selected filters');
        setChartData(null);
        setErrorType(null);
      } else {
        setNoDataMessage(null);
        setChartData(data.chart_data || null);
        setErrorType(null);
      }
      
      // Update sentiment distribution data if available
      if (data.chart_data?.sentiment_distribution?.data) {
        setSentimentDistribution(data.chart_data.sentiment_distribution.data);
      }

      // Update sentiment trend data if available
      if (data.chart_data?.sentiment_trend?.data) {
        setSentimentTrend(data.chart_data.sentiment_trend.data);
      } else {
        setSentimentTrend({
          day: [],
          week: [],
          month: []
        });
      }
        
      setIsValidUser(true);
      setIsValidating(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrorType('connection');
      setIsValidUser(false);
      setIsValidating(false);
    }
  }, [searchParams, timeGrouping]);

  useEffect(() => {
    const userId = searchParams.get('userId');
    if (!userId) {
      setIsValidUser(false);
      setIsValidating(false);
      return;
    }

    // Store the encoded values and fetch decoded values
    const params: FilterParams = {
      userId: userId,
      mediaType: searchParams.get('mediaType') || undefined,
      channel: searchParams.get('channel') || undefined,
      program: searchParams.get('program') || undefined,
      topic: searchParams.get('topic') || undefined,
      startDate: searchParams.get('startDate') || undefined,
      endDate: searchParams.get('endDate') || undefined,
      dateRange: searchParams.get('dateRange') || undefined
    };

    setFilterParams(params);

    // Fetch data if we have params
    if (Object.keys(params).length > 0) {
      fetchData(params);
    }
  }, [searchParams, fetchData]);

  // Handler for time grouping changes
  const handleTimeGroupingChange = (newGrouping: 'day' | 'week' | 'month') => {
    setTimeGrouping(newGrouping);
    // The useEffect will handle the data fetch
  };

  // Transform share of voice data for the pie chart
  const getShareOfVoiceData = () => {
    if (!chartData?.share_of_voice?.data) return [];
    
    const colors = {
      'Televisión': '#FF5733',  // Bright Orange-Red
      'Radio': '#5C33FF',       // Vibrant Indigo
      'Prensa': '#333333',      // Neutral Charcoal Gray
      'Internet': '#00C2FF'     // Bright Cyan
    };
    
    return Object.entries(chartData.share_of_voice.data).map(([name, value]) => ({
      name,
      value,
      color: colors[name as keyof typeof colors] || '#808080' // Fallback color
    }));
  };

  const theme = createWatchdogTheme(darkMode ? 'dark' : 'light');

  // Table sorting handler function
  const handleSort = (column: 'date' | 'media_type' | 'channel' | 'program' | 'sentiment' | 'title' | 'topics') => {
    if (orderBy === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrder('desc');
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Calculate pagination
  const sortedAlerts = [...(chartData?.recent_alerts || [])].sort((a, b) => {
    if (orderBy === 'date') {
      return order === 'asc' 
        ? new Date(a.date).getTime() - new Date(b.date).getTime()
        : new Date(b.date).getTime() - new Date(a.date).getTime();
    }
    if (orderBy === 'sentiment') {
      return order === 'asc' 
        ? a.sentiment - b.sentiment
        : b.sentiment - a.sentiment;
    }
    // Add sorting for string columns
    if (['media_type', 'channel', 'program', 'title'].includes(orderBy)) {
      const aValue = String(a[orderBy as keyof typeof a] || '');
      const bValue = String(b[orderBy as keyof typeof b] || '');
      return order === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    // Special case for topics which is an array
    if (orderBy === 'topics') {
      const aValue = a.topics.filter(topic => topic !== 'Todos los temas').join(', ');
      const bValue = b.topics.filter(topic => topic !== 'Todos los temas').join(', ');
      return order === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    return 0;
  });

  // Filter alerts by selected topic
  const filteredAlerts = selectedTopic 
    ? sortedAlerts.filter(alert => 
        alert.topics.includes(selectedTopic) || 
        (selectedTopic === 'None' && alert.topics.length === 0))
    : sortedAlerts;

  const paginatedAlerts = filteredAlerts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Get all unique topics from all alerts
  const getAllTopics = useCallback(() => {
    if (!chartData?.recent_alerts) return [];
    
    const topicsSet = new Set<string>();
    
    chartData.recent_alerts.forEach(alert => {
      alert.topics.forEach(topic => {
        if (topic !== 'Todos los temas') {
          topicsSet.add(topic);
        }
      });
    });
    
    return Array.from(topicsSet).sort();
  }, [chartData?.recent_alerts]);

  // Handle opening the topics filter menu
  const handleTopicFilterClick = (event: React.MouseEvent<HTMLTableCellElement>) => {
    event.stopPropagation();
    setShowTopicFilter(!showTopicFilter);
  };

  // Handle closing the topics filter menu
  const handleTopicFilterClose = () => {
    setShowTopicFilter(false);
  };

  // Handle selecting a topic filter
  const handleTopicSelect = (topic: string | null) => {
    setSelectedTopic(topic);
    setShowTopicFilter(false);
    // Reset to first page when applying a filter
    setPage(0);
  };

  // Add effect to close topic filter when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        showTopicFilter && 
        topicFilterRef.current && 
        !topicFilterRef.current.contains(event.target as Node) &&
        topicCellRef.current &&
        !topicCellRef.current.contains(event.target as Node)
      ) {
        setShowTopicFilter(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTopicFilter]);

  if (isValidating) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isValidUser) {
    let errorMessage = '';
    switch (errorType) {
      case 'connection':
        errorMessage = 'Unable to connect to Watchdog Analytics API. This is possibly due to a failure to decrypt the userId parameter. Please try again later, or contact support.';
        break;
      case 'missing_user':
        errorMessage = 'User ID parameter is missing from the URL.';
        break;
      case 'invalid_user':
        errorMessage = 'Invalid or expired user ID. Please contact support.';
        break;
      default:
        errorMessage = 'An unexpected error occurred. Cannot connect to Watchdog Analytics API, or the userId parameter might be missing.';
    }
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <WatchdogNoData message={errorMessage} />
      </ThemeProvider>
    );
  }

  if (noDataMessage) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <WatchdogNoData message={noDataMessage} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Main content */}
        <Container 
          maxWidth={false} 
          disableGutters
          sx={{ 
            mt: 4, 
            mb: 4,
            mx: 1,
            maxWidth: '99% !important',
            overflowX: 'hidden'
          }}
        >
          <Grid container spacing={3}>
            {/* Media Type Breakdown Section */}
            <Grid item xs={12}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3,
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.03)'  // Very subtle light overlay in dark mode
                    : 'rgba(0, 0, 0, 0.02)',       // Very subtle dark overlay in light mode
                  backdropFilter: 'blur(8px)',     // Optional: adds a slight blur effect
                  transition: 'background 0.3s',    // Smooth transition when switching themes
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: '30px', // increase bottom margin to 30px
                    fontWeight: 500,
                    color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: theme.palette.mode === 'dark' // add line under chart group title
                      ? '1px solid rgba(255, 255, 255, 0.1)'
                      : '1px solid rgba(0, 0, 0, 0.1)',
                  }}
                >
                  Media Coverage Analysis
                </Typography>
                <Grid container spacing={3}>
                  {/* First Row - Sentiment Trends */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                        <Box 
                          sx={{ 
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {/* Sentiment Trends Title */}
                          <Box 
                            sx={{ 
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              mb: 2
                            }}
                          >
                            <Box
                              sx={{
                                position: 'relative',
                                mb: 2,
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: '-8px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: '60px',
                                  height: '3px',
                                  background: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                                  borderRadius: '2px'
                                }
                              }}
                            >
                              <Typography 
                                variant="h6"
                                sx={{
                                  fontSize: '1.1rem',
                                  fontWeight: 600,
                                  letterSpacing: '1px',
                                  textTransform: 'uppercase',
                                  textAlign: 'center',
                                  color: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '8px'
                                }}
                              >
                                {selectedMetric === 'sentiment' ? 'Sentiment Trends Over Time' : 'Coverage Volume Over Time'}
                                <MUITooltip 
                                  title="This chart displays how media coverage evolves over your selected time period. Toggle between sentiment scores (-1 to +1) or total coverage volume, and adjust the time grouping (daily/weekly/monthly) to analyze trends. Sentiment shows the emotional tone of coverage, while volume shows the quantity of mentions."
                                  componentsProps={{
                                    tooltip: {
                                      sx: { 
                                        fontSize: '0.875rem',
                                        padding: '8px 12px',
                                        maxWidth: 300
                                      }
                                    }
                                  }}
                                >
                                  <InfoIcon 
                                    sx={{ 
                                      fontSize: '1rem',
                                      color: theme.palette.mode === 'dark' ? '#fecf2b' : '#666',
                                      cursor: 'help'
                                    }} 
                                  />
                                </MUITooltip>
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, pr: 20 }}>
                          <ButtonGroup variant="outlined" size="small">
                            <Button 
                              onClick={() => setSelectedMetric('sentiment')}
                              sx={{ 
                                backgroundColor: selectedMetric === 'sentiment' ? '#FFD700' : 'transparent',
                                color: selectedMetric === 'sentiment' ? 'black' : 'inherit',
                                '&:hover': {
                                  backgroundColor: selectedMetric === 'sentiment' ? '#FFD700' : 'rgba(255, 215, 0, 0.1)',
                                  opacity: 0.9
                                }
                              }}
                            >
                              Sentiment
                            </Button>
                            <Button 
                              onClick={() => setSelectedMetric('alert_count')}
                              sx={{ 
                                backgroundColor: selectedMetric === 'alert_count' ? '#FFD700' : 'transparent',
                                color: selectedMetric === 'alert_count' ? 'black' : 'inherit',
                                '&:hover': {
                                  backgroundColor: selectedMetric === 'alert_count' ? '#FFD700' : 'rgba(255, 215, 0, 0.1)',
                                  opacity: 0.9
                                }
                              }}
                            >
                              Alert Count
                            </Button>
                          </ButtonGroup>
                          <Box sx={{ width: '32px' }} /> {/* Spacer */}
                          <ButtonGroup variant="outlined" size="small">
                            <Button 
                              onClick={() => handleTimeGroupingChange('day')}
                              sx={{ 
                                backgroundColor: timeGrouping === 'day' ? '#FFD700' : 'transparent',
                                color: timeGrouping === 'day' ? 'black' : 'inherit',
                                '&:hover': {
                                  backgroundColor: timeGrouping === 'day' ? '#FFD700' : 'rgba(255, 215, 0, 0.1)',
                                  opacity: 0.9
                                }
                              }}
                            >
                              Daily
                            </Button>
                            <Button 
                              onClick={() => handleTimeGroupingChange('week')}
                              sx={{ 
                                backgroundColor: timeGrouping === 'week' ? '#FFD700' : 'transparent',
                                color: timeGrouping === 'week' ? 'black' : 'inherit',
                                '&:hover': {
                                  backgroundColor: timeGrouping === 'week' ? '#FFD700' : 'rgba(255, 215, 0, 0.1)',
                                  opacity: 0.9
                                }
                              }}
                            >
                              Weekly
                            </Button>
                            <Button 
                              onClick={() => handleTimeGroupingChange('month')}
                              sx={{ 
                                backgroundColor: timeGrouping === 'month' ? '#FFD700' : 'transparent',
                                color: timeGrouping === 'month' ? 'black' : 'inherit',
                                '&:hover': {
                                  backgroundColor: timeGrouping === 'month' ? '#FFD700' : 'rgba(255, 215, 0, 0.1)',
                                  opacity: 0.9
                                }
                              }}
                            >
                              Monthly
                            </Button>
                          </ButtonGroup>
                        </Box>
                      </Box>
                      {/* Line Chart */}
                      <Box sx={{ width: '100%', height: '320px' }}>
                        <LineChart 
                          width={chartWidth} 
                          height={320} 
                          data={sentimentTrend[timeGrouping]} 
                          margin={{ top: 5, right: 20, bottom: 45, left: 20 }}
                        >
                          {/* <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#444444' : '#e0e0e0'} /> */}
                          <XAxis 
                            dataKey="date" 
                            angle={-45}
                            textAnchor="end"
                            height={60}
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return timeGrouping === 'day' 
                                ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
                                : timeGrouping === 'week'
                                ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
                                : date.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
                            }}
                          />
                          <YAxis 
                            domain={selectedMetric === 'sentiment' ? [-1.0, 1.0] : [0, 'auto']}
                            tickFormatter={(value) => selectedMetric === 'sentiment' ? value.toFixed(2) : value}
                            tick={{ fontSize: 12, fontWeight: 600 }}
                          />
                          <Tooltip 
                            formatter={(value: number | string, name: string) => [
                              selectedMetric === 'sentiment' ? Number(value).toFixed(2) : value,
                              name.split('_')[0]
                            ]}
                            labelFormatter={(label) => {
                              const date = new Date(label);
                              return date.toLocaleString('en-US', { 
                                month: 'long', 
                                day: 'numeric',
                                year: 'numeric',
                                timeZone: 'UTC'
                              });
                            }}
                            itemStyle={{
                              color: '#5D5580'  // dark grey for value text
                            }}
                          />
                          <Legend 
                            verticalAlign="bottom"
                            height={36}
                            formatter={(value) => value.split('_')[0]}
                          />
                          {selectedMetric === 'sentiment' && (
                            <ReferenceLine 
                              y={0}
                              stroke={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'}
                              strokeWidth={2}
                              strokeDasharray="3 3"
                            />
                          )}
                          {Array.from(
                            new Set(
                              Object.keys(sentimentTrend[timeGrouping]?.[0] || {})
                                .filter(key => key.endsWith(selectedMetric))
                                .map(key => key.split('_')[0])
                             )
                          ).map((mediaType: string, index: number) => {
                            const mediaTypeColors = {
                              'Televisión': '#FF5733',  // Bright Orange-Red
                              'Radio': '#5C33FF',       // Vibrant Indigo
                              'Prensa': '#333333',      // Neutral Charcoal Gray
                              'Internet': '#00C2FF'     // Bright Cyan
                            };
                            return (
                              <Line 
                                key={mediaType}
                                type="monotone" 
                                dataKey={`${mediaType}_${selectedMetric}`}
                                name={mediaType}
                                stroke={mediaTypeColors[mediaType as keyof typeof mediaTypeColors] || '#808080'}
                                strokeWidth={2}
                                dot={{ 
                                  r: 2,
                                  strokeWidth: 1,
                                  fill: mediaTypeColors[mediaType as keyof typeof mediaTypeColors] || '#808080',
                                  stroke: mediaTypeColors[mediaType as keyof typeof mediaTypeColors] || '#808080'
                                }}
                                activeDot={{ 
                                  r: 4,
                                  strokeWidth: 1,
                                  fill: theme.palette.background.paper,
                                  stroke: mediaTypeColors[mediaType as keyof typeof mediaTypeColors] || '#808080'
                                }}
                                connectNulls
                                isAnimationActive={true}
                                animationBegin={0}
                                animationDuration={300}
                              />
                            );
                          })}
                        </LineChart>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Second Row - Distribution Charts */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box 
                          sx={{ 
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mb: 2
                          }}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              mb: 2,
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: '-8px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '60px',
                                height: '3px',
                                background: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                                borderRadius: '2px'
                              }
                            }}
                          >
                            <Typography 
                              variant="h6"
                              sx={{
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                textAlign: 'center',
                                color: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                              }}
                            >
                              Distribution by Media Type
                              <MUITooltip 
                                title="This chart shows the percentage breakdown of media coverage across different types of media (TV, Radio, Press, Internet). The size of each segment represents the proportion of coverage from that media type during the selected time period."
                                componentsProps={{
                                  tooltip: {
                                    sx: { 
                                      fontSize: '0.875rem',
                                      padding: '8px 12px',
                                      maxWidth: 300
                                    }
                                  }
                                }}
                              >
                                <InfoIcon 
                                  sx={{ 
                                    fontSize: '1rem',
                                    color: theme.palette.mode === 'dark' ? '#fecf2b' : '#666',
                                    cursor: 'help'
                                  }} 
                                />
                              </MUITooltip>
                            </Typography>
                          </Box>
                        </Box>
                        <PieChart width={400} height={250}>
                          <Pie
                            data={getShareOfVoiceData()}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {getShareOfVoiceData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box 
                          sx={{ 
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mb: 2
                          }}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              mb: 2,
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: '-8px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '60px',
                                height: '3px',
                                background: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                                borderRadius: '2px'
                              }
                            }}
                          >
                            <Typography 
                              variant="h6"
                              sx={{
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                textAlign: 'center',
                                color: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                              }}
                            >
                              Sentiment Overview
                              <MUITooltip 
                                title="This chart displays the distribution of sentiment across selected media coverage. Positive (above 0.1), Neutral (-0.1 to 0.1), and Negative (below -0.1) percentages show the overall tone of coverage during the selected time period."
                                componentsProps={{
                                  tooltip: {
                                    sx: { 
                                      fontSize: '0.875rem',
                                      padding: '8px 12px',
                                      maxWidth: 300
                                    }
                                  }
                                }}
                              >
                                <InfoIcon 
                                  sx={{ 
                                    fontSize: '1rem',
                                    color: theme.palette.mode === 'dark' ? '#fecf2b' : '#666',
                                    cursor: 'help'
                                  }} 
                                />
                              </MUITooltip>
                            </Typography>
                          </Box>
                        </Box>
                        <BarChart width={400} height={250} data={sentimentDistribution} layout="vertical">
                          {/* <CartesianGrid strokeDasharray="3 3" s/> */}
                          <XAxis type="number" domain={[0, 100]} />
                          <YAxis
                            dataKey="name"
                            type="category"
                            width={100} 
                            tick={{ fontWeight: 600 }}
                            />
                          <Tooltip 
                            itemStyle={{
                              color: '#5D5580'  // dark grey for value text
                            }}
                            />
                          <Bar dataKey="value" fill="#8884d8">
                            {sentimentDistribution.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.name === 'Positive' ? '#5BCF3B' : 
                                      entry.name === 'Neutral' ? '#A0A0A0' : 
                                      '#FF3B3B'}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3,
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.03)'
                    : 'rgba(0, 0, 0, 0.02)',
                  backdropFilter: 'blur(8px)',
                  transition: 'background 0.3s',
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: '30px',
                    fontWeight: 500,
                    color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: theme.palette.mode === 'dark' 
                      ? '1px solid rgba(255, 255, 255, 0.1)'
                      : '1px solid rgba(0, 0, 0, 0.1)',
                  }}
                >
                  Channel Performance Analysis
                </Typography>
                <Grid container spacing={4}>
                  {(chartData?.channel_comparison?.total_channels ?? 0) <= 10 ? (
                    // Single column layout for 10 or fewer channels
                    <Grid item xs={12}>
                      <Box 
                        sx={{ 
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          mb: 2
                        }}
                        >
                        <Box
                          sx={{
                            position: 'relative',
                            mb: 2,
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              bottom: '-8px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '60px',
                              height: '3px',
                              background: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                              borderRadius: '2px'
                            }
                          }}
                          >
                          <Typography
                            variant="h6"
                            align="center"
                            sx={{
                              fontSize: '1.1rem',
                              fontWeight: 600,
                              letterSpacing: '1px',
                              textTransform: 'uppercase',
                              textAlign: 'center',
                              color: theme.palette.mode === 'dark' ? '#fecf2b' : '#333'
                            }}
                            >
                            Channel Performance ({chartData?.channel_comparison?.total_channels} Channels)
                          </Typography>
                        </Box>
                      </Box>
                      <BarChart 
                        width={1000} 
                        height={Math.max(400, (chartData?.channel_comparison?.top_channels.length ?? 0) * 40)} 
                        data={chartData?.channel_comparison?.top_channels || []} 
                        layout="vertical"
                      >
                        {/* <CartesianGrid strokeDasharray="3 3" /> */}
                        <XAxis 
                          type="number" 
                          domain={[ -1, 1 ]}
                        />
                        <YAxis 
                          dataKey="channel" 
                          type="category" 
                          width={150}
                          tick={{ fontSize: 12, fontWeight: 600 }}
                          axisLine={false}
                        />
                        <Tooltip
                          formatter={(value) => Number(value).toFixed(2)}
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            color: 'black'
                          }}
                          itemStyle={{
                            color: '#5D5580'  // dark grey for value text
                          }}
                        />
                        <ReferenceLine x={0} stroke="#666" />
                        <Bar dataKey="sentiment" fill="#FFE599">
                          {(chartData?.channel_comparison?.top_channels || []).map((entry, index) => {
                            const channelColors = [
                              '#FFE599', '#FFD966', '#FFCC33', '#FFB84D', '#FF9933',
                              '#FF6666', '#FFB3B3', '#FFC6A3', '#FFF2CC', '#FFFFB3'
                            ];
                            return <Cell key={`cell-${index}`} fill={channelColors[index % channelColors.length]} />;
                          })}
                        </Bar>
                      </BarChart>
                    </Grid>
                  ) : (
                    // Split layout for more than 10 channels
                    <>
                      <Grid item xs={12} md={6}>
                        <Box 
                          sx={{ 
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mb: 2
                          }}
                          >
                          <Box
                            sx={{
                              position: 'relative',
                              mb: 2,
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: '-8px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '60px',
                                height: '3px',
                                background: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                                borderRadius: '2px'
                              }
                            }}
                            >
                            <Typography variant="h6" align="center" sx={{ 
                              fontSize: '1.1rem',
                              fontWeight: 600,
                              letterSpacing: '1px',
                              textTransform: 'uppercase',
                              textAlign: 'center',
                              color: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px'
                            }}>
                              Top {(chartData?.channel_comparison?.total_channels ?? 0) <= 20 ? 
                                  Math.floor((chartData?.channel_comparison?.total_channels ?? 0) / 2) : 
                                  '10'} Channels
                              <MUITooltip 
                                title="This chart shows the channels with the highest sentiment scores in your selected media coverage. A higher score (closer to +1) indicates more positive coverage from that channel. The chart is sorted from highest to lowest sentiment, helping identify your most favorable media outlets."
                                componentsProps={{
                                  tooltip: {
                                    sx: { 
                                      fontSize: '0.875rem',
                                      padding: '8px 12px',
                                      maxWidth: 300
                                    }
                                  }
                                }}
                              >
                                <InfoIcon 
                                  sx={{ 
                                    fontSize: '1rem',
                                    color: theme.palette.mode === 'dark' ? '#fecf2b' : '#666',
                                    cursor: 'help'
                                  }} 
                                />
                              </MUITooltip>
                            </Typography>
                          </Box>
                        </Box>
                        <BarChart 
                          width={500} 
                          height={400} 
                          data={chartData?.channel_comparison?.top_channels || []} 
                          layout="vertical"
                        >
                          {/* <CartesianGrid strokeDasharray="3 3" /> */}
                          <XAxis 
                            type="number" 
                            domain={[ -1, 1 ]} 
                          />
                          <YAxis 
                            dataKey="channel" 
                            type="category" 
                            width={150}
                            tick={{ fontSize: 12, fontWeight: 600 }}
                            axisLine={false}
                          />
                          <Tooltip
                            formatter={(value) => Number(value).toFixed(2)}
                            contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #ccc',
                              color: 'black'
                            }}
                            itemStyle={{
                              color: '#5D5580'  // dark grey for value text
                            }}
                          />
                          <ReferenceLine x={0} stroke="#666" />
                          <Bar dataKey="sentiment" fill="#FFE599">
                            {(chartData?.channel_comparison?.top_channels || []).map((entry, index) => {
                              const channelColors = [
                                '#FFE599', '#FFD966', '#FFCC33', '#FFB84D', '#FF9933',
                                '#FF6666', '#FFB3B3', '#FFC6A3', '#FFF2CC', '#FFFFB3'
                              ];
                              return <Cell key={`cell-${index}`} fill={channelColors[index % channelColors.length]} />;
                            })}
                          </Bar>
                        </BarChart>
                      </Grid>
                      <Grid item xs={12} md={6}>
                      <Box 
                        sx={{ 
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          mb: 2
                        }}
                        >
                        <Box
                          sx={{
                            position: 'relative',
                            mb: 2,
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              bottom: '-8px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '60px',
                              height: '3px',
                              background: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                              borderRadius: '2px'
                            }
                          }}
                          >
                          <Typography variant="h6" align="center" sx={{ 
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            textAlign: 'center',
                            color: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}>
                            Bottom {(chartData?.channel_comparison?.total_channels ?? 0) <= 20 ? 
                                Math.floor((chartData?.channel_comparison?.total_channels ?? 0) / 2) : 
                                '10'} Channels
                            <MUITooltip 
                              title="This chart shows the channels with the lowest sentiment scores in your selected media coverage. A lower score (closer to -1) indicates more negative coverage from that channel. The chart is sorted from lowest to highest sentiment, helping identify potentially challenging media relationships."
                              componentsProps={{
                                tooltip: {
                                  sx: { 
                                    fontSize: '0.875rem',
                                    padding: '8px 12px',
                                    maxWidth: 300
                                  }
                                }
                              }}
                            >
                              <InfoIcon 
                                sx={{ 
                                  fontSize: '1rem',
                                  color: theme.palette.mode === 'dark' ? '#fecf2b' : '#666',
                                  cursor: 'help'
                                }} 
                              />
                            </MUITooltip>
                          </Typography>
                          </Box>
                        </Box>
                        <BarChart 
                          width={500} 
                          height={400} 
                          data={chartData?.channel_comparison?.bottom_channels || []} 
                          layout="vertical"
                        >
                          {/* <CartesianGrid strokeDasharray="3 3" /> */}
                          <XAxis type="number" domain={[ -1, 1 ]} />
                          <YAxis 
                            dataKey="channel" 
                            type="category" 
                            width={150}
                            tick={{ fontSize: 12, fontWeight: 600 }}
                            axisLine={false}
                          />
                          <Tooltip 
                            formatter={(value) => Number(value).toFixed(2)}
                            contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #ccc',
                              color: 'black'
                            }}
                            itemStyle={{
                              color: '#5D5580'  // dark grey for value text
                            }}
                          />
                          <ReferenceLine x={0} stroke="#666" />
                          <Bar dataKey="sentiment" fill="#DFFF00">
                            {(chartData?.channel_comparison?.bottom_channels || []).map((entry, index) => {
                              const channelColors = [
                                '#DFFF00', '#B6D7A8', '#9FE2BF', '#6FA8DC', '#00AEEF',
                                '#A4C2F4', '#8E7CC3', '#C27BA0', '#E06666', '#FFF1E6'
                              ];
                              return <Cell key={`cell-${index}`} fill={channelColors[index % channelColors.length]} />;
                            })}
                          </Bar>
                        </BarChart>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* Program Breakdown Section */}
            <Grid item xs={12}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3,
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.03)'  // Very subtle light overlay in dark mode
                    : 'rgba(0, 0, 0, 0.02)',       // Very subtle dark overlay in light mode
                  backdropFilter: 'blur(8px)',     // Optional: adds a slight blur effect
                  transition: 'background 0.3s',    // Smooth transition when switching themes
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: '30px',
                    fontWeight: 500,
                    color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: theme.palette.mode === 'dark' 
                      ? '1px solid rgba(255, 255, 255, 0.1)'
                      : '1px solid rgba(0, 0, 0, 0.1)',
                  }}
                >
                  Program Performance Analysis
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                  <Box
                      sx={{
                        position: 'relative',
                        mb: 2,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-8px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '60px',
                          height: '3px',
                          background: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                          borderRadius: '2px'
                        }
                      }}
                    >
                      <Typography 
                        variant="h6"
                        sx={{
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          letterSpacing: '1px',
                          textTransform: 'uppercase',
                          textAlign: 'center',
                          color: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        Share of Voice {' '}
                        <MUITooltip 
                          title="This chart shows the distribution of coverage sentiment for each program. Each bar represents a program's total coverage broken down into Positive (green), Neutral (gray), and Negative (red) percentages, helping identify which programs provide balanced or biased coverage."
                          componentsProps={{
                            tooltip: {
                              sx: { 
                                fontSize: '0.875rem',
                                padding: '8px 12px',
                                maxWidth: 300
                              }
                            }
                          }}
                        >
                          <InfoIcon 
                            sx={{ 
                              fontSize: '1rem',
                              color: theme.palette.mode === 'dark' ? '#fecf2b' : '#666',
                              cursor: 'help'
                            }} 
                          />
                        </MUITooltip>
                      </Typography>
                    </Box>
                    <BarChart width={500} height={400} data={chartData?.program_breakdown?.share_of_voice || []} layout="vertical">
                      {/* <CartesianGrid strokeDasharray="3 3" /> */}
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis 
                        dataKey="program" 
                        type="category" 
                        width={150}
                        tick={{ fontSize: 12, fontWeight: 600 }}
                      />
                      <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                      <Bar dataKey="positive" stackId="a" fill="#5BCF3B" name="Positive" />
                      <Bar dataKey="neutral" stackId="a" fill="#A0A0A0" name="Neutral" />
                      <Bar dataKey="negative" stackId="a" fill="#FF3B3B" name="Negative" />
                      <Legend />
                    </BarChart>
                  </Grid>
                  <Grid item xs={12} md={6}>
                  <Box
                      sx={{
                        position: 'relative',
                        mb: 2,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-8px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '60px',
                          height: '3px',
                          background: theme.palette.mode === 'dark' ? '#fecf2b' : '#333',
                          borderRadius: '2px'
                        }
                      }}
                    >
                      <Typography 
                        variant="h6"
                        sx={{
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          letterSpacing: '1px',
                          textTransform: 'uppercase',
                          textAlign: 'center',
                          color: theme.palette.mode === 'dark' ? '#fecf2b' : '#333'
                        }}
                      >
                        Sentiment Ranking {' '}
                        <MUITooltip 
                          title="This chart displays the overall sentiment score for each program, ranging from -1 (most negative) to +1 (most positive). Programs are ranked from highest to lowest sentiment, helping identify which programs tend to provide more favorable or critical coverage."
                          componentsProps={{
                            tooltip: {
                              sx: { 
                                fontSize: '0.875rem',
                                padding: '8px 12px',
                                maxWidth: 300
                              }
                            }
                          }}
                        >
                          <InfoIcon 
                            sx={{ 
                              fontSize: '1rem',
                              color: theme.palette.mode === 'dark' ? '#fecf2b' : '#666',
                              cursor: 'help'
                            }} 
                          />
                        </MUITooltip>
                      </Typography>
                    </Box>
                    <BarChart width={500} height={400} data={chartData?.program_breakdown?.sentiment_ranking || []} layout="vertical">
                      {/* <CartesianGrid strokeDasharray="3 3" /> */}
                      <XAxis type="number" domain={[-1, 1]} />
                      <YAxis 
                        dataKey="program" 
                        type="category" 
                        width={150}
                        tick={{ fontSize: 12, fontWeight: 600 }}
                        axisLine={false}
                      />
                      <Tooltip
                        formatter={(value) => Number(value).toFixed(2)} 
                        itemStyle={{
                          color: '#5D5580'  // dark grey for value text
                        }}
                      />
                      <ReferenceLine x={0} stroke="#666" /> {/* Add vertical line at x=0 */}
                      <Bar dataKey="sentiment" fill="#FFE07A">
                        {(chartData?.program_breakdown?.sentiment_ranking || []).map((entry, index) => {
                          const programColors = [
                            '#FFE07A', // Soft Yellow-Orange
                            '#FFB800', // Deep Yellow-Orange
                            '#FFD700', // Golden Yellow
                            '#FFA45B', // Peach Orange
                            '#D97A00', // Burnt Orange
                            '#E8C547', // Muted Yellow-Gold
                            '#FFD36E', // Bright Amber
                            '#FF9966', // Coral Orange
                            '#F5A623', // Sunset Orange
                            '#FF6600'  // Bright Tangerine
                          ];
                          return <Cell key={`cell-${index}`} fill={programColors[index % programColors.length]} />;
                        })}
                      </Bar>
                    </BarChart>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Recent Alerts Section */}
            <Grid item xs={12}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3,
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.03)'  // Very subtle light overlay in dark mode
                    : 'rgba(0, 0, 0, 0.02)',       // Very subtle dark overlay in light mode
                  backdropFilter: 'blur(8px)',     // Optional: adds a slight blur effect
                  transition: 'background 0.3s',    // Smooth transition when switching themes
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: '30px',
                    fontWeight: 500,
                    color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: theme.palette.mode === 'dark' 
                      ? '1px solid rgba(255, 255, 255, 0.1)'
                      : '1px solid rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  Latest Media Coverage
                  <MUITooltip
                    title="This table displays your most recent media mentions, sorted by date or sentiment score. Each row shows key details including publication date, media type, channel, program, sentiment score, and associated topics. Click column headers to sort, and use pagination to navigate through all entries. Click 'View' to see the full article in WatchDog."
                    componentsProps={{
                      tooltip: {
                        sx: {
                          fontSize: '0.875rem',
                          padding: '8px 12px',
                          maxWidth: 300
                        }
                      }
                    }}
                  >
                    <InfoIcon 
                      sx={{ 
                        fontSize: '1rem',
                        color: theme.palette.mode === 'dark' ? '#fecf2b' : '#666',
                        cursor: 'help'
                      }} 
                    />
                  </MUITooltip>
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table
                    sx={{ 
                      minWidth: 650,
                      maxWidth: '100%',
                      tableLayout: 'fixed'  // This helps with column widths
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          onClick={() => handleSort('date')}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                          }}
                        >
                          Date {orderBy === 'date' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell 
                          onClick={() => handleSort('media_type')}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                          }}
                        >
                          Media Type {orderBy === 'media_type' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell 
                          onClick={() => handleSort('channel')}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                          }}
                        >
                          Channel {orderBy === 'channel' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell 
                          onClick={() => handleSort('program')}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                          }}
                        >
                          Program {orderBy === 'program' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell 
                          onClick={() => handleSort('sentiment')}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                          }}
                        >
                          Sentiment {orderBy === 'sentiment' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell 
                          onClick={() => handleSort('title')}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                          }}
                        >
                          Title {orderBy === 'title' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell 
                          ref={topicCellRef}
                          onClick={handleTopicFilterClick}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                            position: 'relative'
                          }}
                        >
                          Topics {selectedTopic ? ` (${selectedTopic})` : ''}
                          <Box 
                            component="span" 
                            sx={{ 
                              marginLeft: '4px',
                              color: theme.palette.mode === 'dark' ? '#FECF2B' : '#CF9307',
                              fontSize: '16px'
                            }}
                          >
                            ▾
                          </Box>

                          {/* Custom dropdown that doesn't affect page scrolling */}
                          {showTopicFilter && (
                            <Box
                              ref={topicFilterRef}
                              sx={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                zIndex: 1000,
                                width: '250px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                backgroundColor: theme.palette.background.paper,
                                boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
                                borderRadius: '4px',
                                mt: 1
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <List dense sx={{ padding: 0 }}>
                                <ListItemButton 
                                  onClick={() => handleTopicSelect(null)} 
                                  sx={{ 
                                    backgroundColor: selectedTopic === null ? 'rgba(254, 207, 43, 0.15)' : 'transparent',
                                    fontWeight: selectedTopic === null ? 'bold' : 'normal',
                                    padding: '8px 16px'
                                  }}
                                >
                                  <ListItemText primary="All Topics" />
                                </ListItemButton>
                                <Divider />
                                {getAllTopics().map((topic) => (
                                  <ListItemButton 
                                    key={topic} 
                                    onClick={() => handleTopicSelect(topic)}
                                    sx={{ 
                                      backgroundColor: topic === selectedTopic ? 'rgba(254, 207, 43, 0.15)' : 'transparent',
                                      fontWeight: topic === selectedTopic ? 'bold' : 'normal',
                                      padding: '8px 16px'
                                    }}
                                  >
                                    <ListItemText 
                                      primary={topic} 
                                      primaryTypographyProps={{ 
                                        style: { 
                                          wordBreak: 'break-word',
                                          whiteSpace: 'normal'
                                        } 
                                      }}
                                    />
                                  </ListItemButton>
                                ))}
                              </List>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedAlerts.map((alert, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {new Date(alert.date).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: 'numeric'
                            })}
                          </TableCell>
                          <TableCell>{alert.media_type}</TableCell>
                          <TableCell>{alert.channel}</TableCell>
                          <TableCell>{alert.program}</TableCell>
                          <TableCell>
                            <Box 
                              sx={{ 
                                backgroundColor: 
                                  alert.sentiment > 0.1 ? '#5BCF3B' :
                                  alert.sentiment < -0.1 ? '#FF3B3B' : '#A0A0A0',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                display: 'inline-block',
                                color: 'black'
                              }}
                            >
                              {alert.sentiment.toFixed(2)}
                            </Box>
                          </TableCell>
                          <TableCell>{alert.title}</TableCell>
                          <TableCell>
                            <MUITooltip 
                              title={alert.topics.filter(topic => topic !== 'Todos los temas').join(', ')}
                              componentsProps={{
                                tooltip: {
                                  sx: { 
                                    fontSize: '1rem',
                                    padding: '8px 12px'
                                  }
                                }
                              }}
                              >
                              <span>
                                {alert.topics.filter(topic => topic !== 'Todos los temas').join(', ').length > 50 
                                  ? `${alert.topics.filter(topic => topic !== 'Todos los temas').join(', ').substring(0, 50)}...` 
                                  : alert.topics.filter(topic => topic !== 'Todos los temas').join(', ')}
                              </span>
                            </MUITooltip>
                          </TableCell>
                          <TableCell>
                            <Link 
                              href={`https://clients.watchdogpr.net/alert/${alert.alert_id}/`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              sx={{ 
                                color: '#CF9307',
                                fontWeight: 500,
                                '&:hover': {
                                  color: '#E6A408',
                                  textDecoration: 'none'
                                }
                              }}
                            >
                              View
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={filteredAlerts.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10]}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default WatchdogDashboard; 